import { stickyBottomBarFirstTriggerStyles } from "components/StickyBottomBar";
import StickyBottomBarTrigger from "components/StickyBottomBar/StickyBottomBarTrigger";
import { DataContextProvider } from "contexts/DataContext";
import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import { MobileMenuContextProvider } from "contexts/MobileMenuContext";
import { ModalContextProvider } from "contexts/ModalContext";
import { StickyBottomBarContextProvider } from "contexts/StickyBottomBarContext";
import CustomHead from "modules/common/components/CustomHead";
import Footer from "modules/common/components/Footer";
import Grid from "modules/common/components/Grid";
import { MenuItems, HeaderNav, CheckCompensationButton } from "modules/common/components/HeaderNav";
import { PressLogos } from "modules/common/components/PressLogos";
import { HERO_CHECK_COMPENSATION_GTM, HERO_CHECK_COMPENSATION_MP } from "modules/home-page/trackingConsts";
import BreadcrumbsSchema from "modules/magnitude/components/BreadcrumbsSchema";
import NextLink from "next/link";
import { getPageUrl } from "utils/getPageUrl";
import { useTopMenu } from "utils/menuUtils";
import { trackEvents } from "utils/trackingUtils";
import {
  ChakraBox as Box,
  ChakraHeading as Heading,
  ChakraUnorderedList as UnorderedList,
  ChakraListItem as ListItem,
  ChakraStack as Stack,
  ChakraText as Text,
  ChakraLink as Link,
  ChakraButton as Button,
  ChakraDivider as Divider,
} from "@airhelp/react-ui-components";
import type { MagnitudeTopicTemplateProps } from ".";
import StickyBottomBarAdapter from "../../../storyblok/adapters/StickyBottomBar";

const MagnitudeTopicTemplate = ({
  data,
  topic,
  cmsLanguagesData,
  whiteListDomains = [],
  serverSideStoryblokPages,
}: MagnitudeTopicTemplateProps) => {
  const language = useLanguage();
  const translations = useTranslations();
  const topMenu = useTopMenu(language);
  const { metaTitle, metaDescription, headingText, introText, pages } = data;
  const trackingEvents = { gtmEvent: HERO_CHECK_COMPENSATION_GTM, mixpanelEvent: HERO_CHECK_COMPENSATION_MP };
  const breadcrumbsPositionTranslationKeyMap = { airlines: "menu_airlines", airports: "menu_airports" };
  const breadcrumbsPositionName = breadcrumbsPositionTranslationKeyMap[topic];

  return (
    <StickyBottomBarContextProvider threshold={0.4}>
      <DataContextProvider data={{ cmsLanguagesData, serverSideStoryblokPages, whiteListDomains }}>
        <CustomHead title={metaTitle} description={metaDescription} />
        <BreadcrumbsSchema currentPositionName={translations(breadcrumbsPositionName)} />
        <MobileMenuContextProvider>
          <ModalContextProvider>
            <HeaderNav menuItems={<MenuItems menu={topMenu} />} headerButtons={<CheckCompensationButton />} />
            <Grid pt={32} as="main">
              <StickyBottomBarTrigger type="first" styles={stickyBottomBarFirstTriggerStyles} />
              <Box gridColumn={{ base: "initial", md: "2 / span 10", lg: "3 / span 8" }}>
                <Heading variant="h1" as="h1" mb={6}>
                  {headingText}
                </Heading>
                <Text fontSize="lg">{introText}</Text>
                <Button
                  mt={6}
                  variant="secondary"
                  as="a"
                  href={getPageUrl(language, "webappNewClaim")}
                  title={translations("check_compensation")}
                  onClick={() => trackEvents(trackingEvents)}
                >
                  {translations("check_compensation")}
                </Button>
                <Divider mb={16} pt={16} />
                <UnorderedList>
                  {pages &&
                    pages.map((page) => (
                      <ListItem key={page.id} mt={3}>
                        <NextLink href={`/${language}/${topic}/${page.slug}/`} passHref>
                          <Link color="primary.500" title={page.title} fontSize="xl">
                            {page.title}
                          </Link>
                        </NextLink>
                      </ListItem>
                    ))}
                </UnorderedList>
                <Stack spacing={6} />
              </Box>
            </Grid>
            <PressLogos />
            <Footer />
          </ModalContextProvider>
        </MobileMenuContextProvider>
        <StickyBottomBarAdapter />
      </DataContextProvider>
    </StickyBottomBarContextProvider>
  );
};

export default MagnitudeTopicTemplate;
